import {
  KNOWLEDGE_ITEM_TYPES,
  saveKnowledgeItem,
  saveKnowledgeItemWithSupersession,
} from '../knowledgeItems';
import { getCareKnowledgePromotionDecision } from './careKnowledgeDurabilityPolicy';
import { CAREGIVER_PROFILE_ADD_EXTRACTION_VERSION } from '../knowledgeItems/caregiverProfileFact';
import {
  PROFILE_PLACEMENT_MODES,
  buildExplicitProfilePlacementMetadata,
} from '../profileContract/profilePlacementMetadata';
import { markCareKnowledgeSourceReviewComplete } from './careSourceActivityService';
import { saveTextCareSourceForProcessing } from './careSourceCaptureSubmitters';
import {
  DIRECT_PROFILE_KNOWLEDGE_PERSISTENCE_MODE,
  DIRECT_PROFILE_KNOWLEDGE_SOURCE_ITEM_ID,
  getDirectProfileOrganizationMode,
} from './directProfileKnowledgeModel';

const cleanText = (value = '') => String(value || '').replace(/\s+/g, ' ').trim();
const buildTitle = (value = '') => {
  const text = cleanText(value);
  if (text.length <= 160) return text;
  return `${text.slice(0, 157).trimEnd()}...`;
};

export const saveExplicitProfileKnowledgeText = async ({
  childId = '',
  rawText = '',
  profileChapterId = '',
  profileField = '',
  profilePlacementMode = '',
  supersededKnowledgeItemId = '',
  deps = {},
} = {}) => {
  const originalText = String(rawText || '').trim();
  const profileOrganizationMode = getDirectProfileOrganizationMode(originalText);
  const text = cleanText(originalText);
  const placement = buildExplicitProfilePlacementMetadata({
    profileChapterId,
    profileField,
    profilePlacementMode,
  });
  if (!childId || !text) throw new Error('Missing caregiver Profile information.');
  if (placement.profilePlacementMode !== PROFILE_PLACEMENT_MODES.EXPLICIT_CAREGIVER) {
    throw new Error('Missing explicit Profile placement.');
  }

  const saveSource = deps.saveTextCareSourceForProcessing || saveTextCareSourceForProcessing;
  const saveItem = deps.saveKnowledgeItem || saveKnowledgeItem;
  const saveWithSupersession = deps.saveKnowledgeItemWithSupersession || saveKnowledgeItemWithSupersession;
  const markSourceComplete = deps.markCareKnowledgeSourceReviewComplete || markCareKnowledgeSourceReviewComplete;
  const sourceResult = await saveSource({
    childId,
    rawText: originalText,
    sourceType: 'care_knowledge',
    sourceName: 'Typed care info',
    inputMode: 'care_knowledge_text',
    sourceContext: 'knowledge',
    ...placement,
    knowledgePersistenceMode: DIRECT_PROFILE_KNOWLEDGE_PERSISTENCE_MODE,
    profileOrganizationMode,
  });
  const sourceId = cleanText(sourceResult?.sourceNoteId || sourceResult?.id);
  if (!sourceId) throw new Error('The caregiver source was saved without a source id.');

  // A complex entry is a source containing several facts, not one oversized
  // fact. The existing background organizer owns splitting it. Saving a
  // Knowledge item here would make the full paragraph appear in Profile and
  // later create duplicates beside the organized facts.
  if (profileOrganizationMode === 'complex') {
    return {
      ...sourceResult,
      knowledgeItem: null,
      knowledgeItemId: '',
      savedItems: [],
      profileOrganizationMode,
    };
  }

  const sourceTitle = 'Typed care info';
  const sourceProvenance = {
    origin: 'caregiver_typed',
    label: 'Caregiver-typed',
    sourceKind: 'caregiver_direct',
    editableByCaregiver: true,
  };
  const knowledgeItem = {
    childId,
    captureId: sourceId,
    knowledgeType: KNOWLEDGE_ITEM_TYPES.PROFILE,
    title: buildTitle(text),
    summary: text,
    sourceText: originalText,
    sourceRefs: [{
      sourceId,
      sourceTitle,
      sourceType: 'care_knowledge',
      sourceContext: 'knowledge',
      sourceCollection: 'careSources',
      sourceText: originalText,
      confidence: 'high',
    }],
    sourceProvenance,
    extractionVersion: CAREGIVER_PROFILE_ADD_EXTRACTION_VERSION,
    editedByUser: true,
    confidence: 'high',
    capturedBy: {
      sourceDocumentId: sourceId,
      sourceContext: 'knowledge',
      authorship: 'caregiver',
    },
    evidenceIdentity: {
      sourceCollection: 'careSources',
      sourceId,
      sourceItemId: DIRECT_PROFILE_KNOWLEDGE_SOURCE_ITEM_ID,
    },
    ...placement,
  };
  const promotionDecision = getCareKnowledgePromotionDecision(knowledgeItem);
  const savedItem = supersededKnowledgeItemId
    ? await saveWithSupersession(knowledgeItem, supersededKnowledgeItemId)
    : await saveItem(knowledgeItem);

  const enrichedSavedItem = {
    ...savedItem,
    sourceProvenance,
    promotionDecision,
    needsCaregiverDecision: promotionDecision.needsCaregiverDecision,
  };

  const savedSummary = {
    id: enrichedSavedItem.id,
    clientId: DIRECT_PROFILE_KNOWLEDGE_SOURCE_ITEM_ID,
    knowledgeType: enrichedSavedItem.knowledgeType || KNOWLEDGE_ITEM_TYPES.PROFILE,
    title: enrichedSavedItem.title || buildTitle(text),
    content: enrichedSavedItem.summary || text,
    summary: enrichedSavedItem.summary || text,
    sourceText: enrichedSavedItem.sourceText || originalText,
    confidence: 'high',
    sourceProvenance,
    promotionDecision,
  };
  await markSourceComplete(sourceId, { savedItems: [savedSummary] });

  return {
    ...sourceResult,
    knowledgeItem: enrichedSavedItem,
    knowledgeItemId: enrichedSavedItem.id,
    savedItems: [savedSummary],
    profileOrganizationMode,
  };
};
